/**
 * Demo/mock pack dataset (Task 1) — a set of READY orders that exercise every
 * pack scenario WITHOUT touching real Shopify/DB: single-item, multi-item, a
 * no-barcode line (manual-confirm path), a multi-box candidate, and a short-stock
 * case; wrong-item + over-scan are exercised by scanning the wrong/extra codes.
 * Namespaced with the "DEMO" tag and #DEMO-* names so it never mixes with real
 * ingested orders. Served to the queue + the pack station only in mock mode.
 *
 * Pure data (full OrderDTO/OrderLineDTO) so the existing pack state machine
 * (machine.ts) runs against it unchanged.
 */
import type { OrderDTO, OrderLineDTO, StoreRegion } from "@cairn/shared";
import type { DocAddress } from "../lib/api.js";
import type { SkuBarcodeAlias } from "./machine.js";

let lineSeq = 0;
function line(p: Partial<OrderLineDTO> & { title: string; quantity: number }): OrderLineDTO {
  lineSeq += 1;
  return {
    id: `dline-${lineSeq}`,
    shopifyLineId: `gid://demo/LineItem/${lineSeq}`,
    sku: p.sku ?? null,
    barcode: p.barcode ?? null,
    title: p.title,
    variantTitle: p.variantTitle ?? null,
    quantity: p.quantity,
    quantityPacked: 0,
    unitWeightG: p.unitWeightG ?? 0,
    retailUnitPrice: p.retailUnitPrice ?? 0,
    cogsUnitPrice: p.cogsUnitPrice ?? null,
    hsCode: p.hsCode ?? null,
    countryOfOrigin: p.countryOfOrigin ?? null,
    requiresCustoms: p.requiresCustoms ?? false,
    shippable: p.shippable ?? true,
    isBundleParent: p.isBundleParent ?? false,
    properties: p.properties ?? null,
  };
}

function order(p: {
  id: string;
  name: string;
  region?: StoreRegion;
  shippingCountry?: string;
  method?: string;
  carrier?: string | null;
  service?: string | null;
  serviceClass?: OrderDTO["serviceClass"];
  pickupName?: string | null;
  lines: OrderLineDTO[];
}): OrderDTO {
  const itemsWeightG = p.lines.reduce((s, l) => s + (l.unitWeightG ?? 0) * l.quantity, 0);
  return {
    id: p.id,
    storeId: "EU",
    storeRegion: p.region ?? "EU",
    shopifyOrderId: p.id,
    shopifyOrderGid: `gid://demo/Order/${p.id}`,
    name: p.name,
    status: "READY",
    fulfilmentType: p.pickupName ? "CARRIER_PICKUP_POINT" : "CARRIER_SHIPMENT",
    financialStatus: "paid",
    fulfillmentStatus: "unfulfilled",
    shippingCountry: p.shippingCountry ?? "DE",
    shippingMethodTitle: p.method ?? "GLS Standard Delivery",
    serviceClass: p.serviceClass ?? "STANDARD",
    currency: "EUR",
    totalPrice: p.lines.reduce((s, l) => s + l.retailUnitPrice * l.quantity, 0),
    itemsWeightG,
    theoreticalWeightG: itemsWeightG, // mock scale "in tolerance" target
    carrierCode: p.carrier === undefined ? "GLS" : p.carrier,
    serviceCode: p.service === undefined ? "GLS_BUSINESS_PARCEL" : p.service,
    pickupPoint: p.pickupName ? { carrier: "GLS", pointId: "GLS-locker:DEMO", name: p.pickupName } : null,
    hasPickupPoint: Boolean(p.pickupName),
    tags: ["DEMO", "SHIPPING"],
    productTags: [],
    createdAtShopify: new Date(Date.now() - 30 * 60_000).toISOString(),
    lines: p.lines,
  };
}

export const DEMO_ORDERS: OrderDTO[] = [
  order({
    id: "demo-1",
    name: "#DEMO-101",
    method: "GLS Standard Delivery",
    lines: [
      line({ title: "Explorer Beanie — Ash", sku: "AN_EB_AH", barcode: "44102991", quantity: 1, unitWeightG: 95, retailUnitPrice: 16 }),
    ],
  }),
  order({
    id: "demo-2",
    name: "#DEMO-102",
    shippingCountry: "FI",
    method: "GLS Standard Delivery",
    lines: [
      line({ title: "Sierra Flannel Shirt — Nightfall", sku: "AN_SIER_NF_M", barcode: "92529230", quantity: 1, unitWeightG: 445, retailUnitPrice: 59.94 }),
      line({ title: "Summit Hike Crew Socks — Carbon", sku: "SOCK_SC_CB_M", barcode: "72646118", quantity: 2, unitWeightG: 80, retailUnitPrice: 15.92, properties: { gift_note: "Happy trails!" } }),
      line({ title: "Momentum Sweater — Mulberry", sku: "MOM_SW_MU_M", barcode: "55239242", quantity: 1, unitWeightG: 520, retailUnitPrice: 38.94 }),
    ],
  }),
  order({
    id: "demo-3",
    name: "#DEMO-103",
    shippingCountry: "NL",
    method: "GLS PAKETOMAT - Lizin vrt • 0.3km",
    service: "GLS_SHOP_DELIVERY",
    pickupName: "GLS Paketomat — Lizin vrt",
    lines: [
      line({ title: "Trailhead Cap — Olive", sku: "AN_TC_OL", barcode: "30119284", quantity: 1, unitWeightG: 110, retailUnitPrice: 22 }),
      // No barcode → exercises the manual-confirm path (§14.1).
      line({ title: "Alpine Patch (no barcode)", sku: "AN_PATCH", barcode: null, quantity: 1, unitWeightG: 15, retailUnitPrice: 6 }),
    ],
  }),
  order({
    id: "demo-4",
    name: "#DEMO-104",
    shippingCountry: "DE",
    method: "DHL Express Worldwide",
    carrier: "DHL",
    service: "EXPRESS_WORLDWIDE",
    serviceClass: "EXPRESS",
    lines: [
      // Heavy + high qty → a multi-box candidate (§14.2).
      line({ title: "Glacier Hardshell Jacket — Black", sku: "AN_GHJ_BK_L", barcode: "81002277", quantity: 6, unitWeightG: 1200, retailUnitPrice: 219 }),
    ],
  }),
  order({
    id: "demo-5",
    name: "#DEMO-105",
    shippingCountry: "HR",
    method: "GLS Standard Delivery",
    lines: [
      line({ title: "Ridge Merino Base Layer — Slate", sku: "AN_RML_SL_M", barcode: "60554417", quantity: 3, unitWeightG: 230, retailUnitPrice: 49 }),
      line({ title: "Explorer Beanie — Ash", sku: "AN_EB_AH", barcode: "44102991", quantity: 1, unitWeightG: 95, retailUnitPrice: 16 }),
    ],
  }),
];

/** A barcode alias (§29.2) so the alias-matching path is exercised on DEMO-101. */
export const DEMO_ALIASES: SkuBarcodeAlias[] = [{ sku: "AN_EB_AH", barcode: "ALT-BEANIE-001" }];

/**
 * Demo product thumbnails (§Task3). Inline SVG data-URIs so they render offline
 * in preview/screenshots — real ingests carry Shopify CDN URLs. Keyed by SKU;
 * AN_PATCH is intentionally absent to exercise the no-image fallback tile.
 */
function tile(label: string, c1: string, c2: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c1}"/><stop offset="1" stop-color="${c2}"/></linearGradient></defs><rect width="80" height="80" rx="10" fill="url(#g)"/><text x="40" y="48" font-family="system-ui,sans-serif" font-size="26" font-weight="700" fill="#fff" text-anchor="middle">${label}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
export const DEMO_LINE_IMAGES: Record<string, string> = {
  AN_EB_AH: tile("EB", "#0ea5b7", "#155e75"),
  AN_SIER_NF_M: tile("SF", "#6366f1", "#3730a3"),
  SOCK_SC_CB_M: tile("SK", "#64748b", "#334155"),
  MOM_SW_MU_M: tile("SW", "#a855f7", "#6b21a8"),
  AN_TC_OL: tile("TC", "#65a30d", "#3f6212"),
  AN_GHJ_BK_L: tile("HJ", "#0f172a", "#334155"),
  AN_RML_SL_M: tile("BL", "#0891b2", "#155e75"),
};

/** Recipient + payment detail for a demo order's packing slip (Task 5). */
export interface DemoRecipient {
  email: string;
  paymentMethod: string;
  shipTo: DocAddress;
  /** null → "same as delivery address" on the slip. */
  billTo: DocAddress | null;
}

/**
 * Per-demo-order recipients (Task 5). Real orders carry the Shopify shipping +
 * billing address; here we attach plausible EU recipients matching each order's
 * destination country so the packing slip looks like a real document.
 */
export const DEMO_RECIPIENTS: Record<string, DemoRecipient> = {
  "demo-1": {
    email: "lena.vogt@example.de",
    paymentMethod: "Shopify Payments · Visa •••• 4242",
    shipTo: { name: "Lena Vogt", street1: "Elsa-Brändström-Str. 16c", city: "Dresden", postcode: "01219", country: "DE", phone: "+49 351 1234567" },
    billTo: null,
  },
  "demo-2": {
    email: "aada.korhonen@example.fi",
    paymentMethod: "Shopify Payments · Mastercard •••• 5318",
    shipTo: { name: "Aada Korhonen", company: "Korhonen Design Oy", street1: "Mannerheimintie 12", street2: "A 4", city: "Helsinki", postcode: "00100", country: "FI", phone: "+358 40 1234567" },
    billTo: { name: "Korhonen Design Oy", street1: "Mannerheimintie 12", city: "Helsinki", postcode: "00100", country: "FI" },
  },
  "demo-3": {
    email: "pieter.devries@example.nl",
    paymentMethod: "PayPal · pieter.devries@example.nl",
    shipTo: { name: "Pieter de Vries", street1: "Prinsengracht 263", city: "Amsterdam", postcode: "1016 GV", country: "NL", phone: "+31 6 12345678" },
    billTo: null,
  },
  "demo-4": {
    email: "thomas.weber@example.de",
    paymentMethod: "Shopify Payments · Amex •••• 1009",
    shipTo: { name: "Thomas Weber", street1: "Maximilianstraße 35", city: "Munich", province: "Bavaria", postcode: "80539", country: "DE", phone: "+49 89 7654321" },
    billTo: null,
  },
  "demo-5": {
    email: "ana.maric@example.hr",
    paymentMethod: "Shopify Payments · Visa •••• 7781",
    shipTo: { name: "Ana Marić", street1: "Ilica 42", city: "Zagreb", postcode: "10000", country: "HR", phone: "+385 91 1234567" },
    billTo: null,
  },
};

export function findDemoOrder(idOrName: string): OrderDTO | undefined {
  const q = idOrName.trim().toLowerCase();
  return DEMO_ORDERS.find((o) => o.id.toLowerCase() === q || o.name.toLowerCase() === q);
}

/** Per-order scan cheatsheet for the keyboard-only demo (no physical scanner). */
export function demoScanHints(o: OrderDTO): Array<{ label: string; code: string; note?: string }> {
  return o.lines
    .filter((l) => l.shippable && !l.isBundleParent)
    .map((l) => ({
      label: `${l.title}${l.quantity > 1 ? ` ×${l.quantity}` : ""}`,
      code: l.barcode ?? l.sku ?? "",
      note: l.barcode ? undefined : "no barcode — use Manual confirm",
    }));
}
